<div class="flex border-b-2 border-primary">
  <div class="flex-1 p-2">
    <h1 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 text-center">Manage Users' Assigned Locations</h1>
    <p class="font-normal text-gray-700 text-center">Search by user to manage their assigned locations</p>
  </div>

  @if (selectedUser(); as user) {
    <div class="flex-1 flex items-center pl-2 italic">{{ user.name }} ({{ user.email }})</div>
  }
</div>

<div class="flex h-full">
  <div class="flex-1 flex flex-col h-[600px] border-r-2 border-b-2 border-primary">
    <div class="flex mx-[-1px] items-center relative">
      <input
        panInput
        color="primary"
        class="w-full !rounded-none mt-[-1px]"
        placeholder="Filter {{ totalUsers() }} users..."
        [(ngModel)]="userFilter"
        (ngModelChange)="searchInput.next($event)"
      />

      <app-pan-button
        class="absolute right-[10px]"
        size="sm"
        color="info"
        (click)="newUserModal.show()"
        panRole
        [roles]="['admin', 'branch-manager', 'branch-owner']"
        >Add User</app-pan-button
      >
    </div>

    <div class="flex-1 overflow-hidden overflow-y-auto">
      @if (visibleUsers().length === 0 && !hasLoadedPage()) {
        <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading... </app-pan-blank-slate>
      } @else if (visibleUsers().length === 0 && hasLoadedPage()) {
        <app-pan-blank-slate>
          No users match your query.

          <app-pan-button
            icon="add"
            color="primary"
            fill="outline"
            (click)="newUserModal.show()"
            panRole
            [roles]="['admin', 'branch-manager', 'branch-owner']"
            >Add one</app-pan-button
          >
        </app-pan-blank-slate>
      } @else {
        <cdk-virtual-scroll-viewport #userScroll itemSize="50" class="h-full" data-testid="userlocations-user-list">
          <div
            *cdkVirtualFor="let item of visibleUsers()"
            class="h-[50px] p-3 flex items-center border-b-2 cursor-pointer"
            [class.bg-primary-no-hover]="item === selectedUser()"
            [attr.data-testid]="'userlocations-user-' + item.email"
            (click)="selectUser(item)"
          >
            <div class="flex flex-col">
              <div>{{ item.name }}</div>
              <div class="italic text-sm">{{ item.email }}</div>
            </div>
          </div>

          @if (isLoadingMoreUsers()) {
            <div class="h-[50px] p-3 flex items-center">
              <app-pan-spinner></app-pan-spinner>
            </div>
          }
        </cdk-virtual-scroll-viewport>
      }
    </div>
  </div>

  <div class="flex-1 h-[600px] overflow-hidden border-r-2 border-b-2 border-primary">
    @if (!hasLoadedLocations()) {
      <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading locations... </app-pan-blank-slate>
    } @else {
      @if (visibleUsers().length > 0) {
        <div class="flex mx-[-1px] items-center relative">
          <input
            panInput
            color="primary"
            class="w-full !rounded-none mt-[-1px]"
            placeholder="Filter locations..."
            [(ngModel)]="locationFilter"
          />

          @if (selectedUser()) {
            <app-pan-button data-testid="add-location-button" class="absolute right-[10px]" size="sm" color="info" (click)="addLocationModal.show()"
              >Add Location</app-pan-button
            >
          }
        </div>
      }

      @if (!selectedUser()) {
        <app-pan-blank-slate>Select a user to see locations for it.</app-pan-blank-slate>
      } @else if (selectedUser() && visibleUserLocations().length === 0) {
        <app-pan-blank-slate>
          There are no locations assigned to this user.

          <app-pan-button icon="add" color="primary" fill="outline" (click)="addLocationModal.show()">Add one</app-pan-button>
        </app-pan-blank-slate>
      } @else if (selectedUser() && locationFilter() && visibleUserLocations().length === 0) {
        <app-pan-blank-slate>
          No locations match your query.

          <app-pan-button icon="add" color="primary" fill="outline" (click)="addLocationModal.show()">Add one</app-pan-button>
        </app-pan-blank-slate>
      } @else {
        <cdk-virtual-scroll-viewport [itemSize]="75" class="h-[560px]">
          <div *cdkVirtualFor="let item of visibleUserLocations()" class="h-[75px] p-3 flex items-center border-b-2 flex-row">
            @let user = selectedUser();

            <div class="flex-1 flex flex-col">
              <div data-testid="location-name">
                {{ item.name }}
              </div>

              <div class="text-sm italic">
                {{ item.branch_name }}
              </div>
            </div>

            <div class="flex-1 mr-2">
              <ng-select
                data-testid="location-roles-selector"
                appendTo="body"
                placeholder="Select roles..."
                [bindValue]="'id'"
                [bindLabel]="'name'"
                [virtualScroll]="true"
                [items]="item._selectableRoles"
                [(ngModel)]="user.roles.location[item.id]"
                [multiple]="true"
                [clearable]="false"
                (add)="addUserRole(user, $event, item)"
                (remove)="removeUserRole(user, $event, item)"
              >
                <ng-template ng-label-tmp let-role="item" let-clear="clear">
                  <span class="ng-value-label">{{ role.name }}</span>

                  @if (userLogin.canGrantLocationPermission(role.id, item.branch_id.toString(), item.id.toString())) {
                    <span class="ng-value-icon right" (click)="clear(role)" aria-hidden="true">×</span>
                  }
                </ng-template>
              </ng-select>
            </div>

            <app-pan-button color="danger" icon="delete" [swal]="removePermissionsSwal"></app-pan-button>

            <swal
              #removePermissionsSwal
              title="Remove User Location Permissions?"
              text="Are you sure you want to remove permissions for {{ selectedUser().name || selectedUser().email }}?"
              icon="warning"
              confirmButtonText="Yes, Remove"
              [showCancelButton]="true"
              [focusCancel]="true"
              (confirm)="removeUserPermissions(selectedUser(), item)"
            >
            </swal>
          </div>
        </cdk-virtual-scroll-viewport>
      }
    }
  </div>
</div>

<app-admin-user-management-create-user
  #newUserModal
  (createdUser)="addUserToList($event); newUserModal.hide()"
></app-admin-user-management-create-user>

<app-pan-modal #addLocationModal [topLevel]="false">
  <div header>Add User Location</div>

  <div body class="flex flex-col gap-2">
    <app-page-select-location [(selected)]="modalLocation" [overrideLocations]="allLocations()"></app-page-select-location>

    @if (modalLocation(); as chosenLocation) {
      <ng-select
        class="w-full"
        data-testid="modal-location-roles-selector"
        placeholder="Select roles..."
        [bindValue]="'id'"
        [bindLabel]="'name'"
        [virtualScroll]="true"
        [items]="chosenLocation._selectableRoles ?? []"
        [(ngModel)]="modalRoles"
        [multiple]="true"
        [clearable]="false"
      >
        <ng-template ng-label-tmp let-item="item" let-clear="clear">
          <span class="ng-value-label">{{ item.name }}</span>

          @if (userLogin.canGrantLocationPermission(item.id, chosenLocation.branch_id.toString(), chosenLocation.id.toString())) {
            <span class="ng-value-icon right" (click)="clear(item)" aria-hidden="true">×</span>
          }
        </ng-template>
      </ng-select>
    }
  </div>

  <div footer>
    <button panButton panModalClose color="primary" (click)="addLocationToUser()" [disabled]="!canSubmitModal()">
      Add Location To User
    </button>
  </div>
</app-pan-modal>
